import Avatar from "@mui/material/Avatar"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"

export function initials(name, phone) {
  const source = (name || phone || "?").trim()
  const parts = source.split(/[\s@._+-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return source.replace(/\D/g, "").slice(-2) || source.slice(0, 2).toUpperCase()
}

function formatMessageTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return format(date, "MMM d, yyyy · h:mm a")
}

/** One SMS bubble; shared by the SMS inbox and the admin activity thread. */
export default function SmsMessageCard({ message }) {
  const inbound = Boolean(message.is_inbound)
  const name = inbound
    ? message.candidate_name || message.contact_name || message.from_address
    : message.sender_name || message.from_address
  const phone = inbound
    ? message.from_address
    : message.to_address || message.from_address
  const senderEmail =
    !inbound &&
    message.sender_email &&
    String(message.sender_email).toLowerCase() !==
      String(name || "").toLowerCase()
      ? message.sender_email
      : ""
  const showPhone =
    Boolean(phone) &&
    String(phone).toLowerCase() !== String(name || "").toLowerCase()
  const details = [
    senderEmail,
    showPhone ? `${inbound ? "" : "To "}${phone}` : "",
    message.received_at ? formatMessageTime(message.received_at) : "",
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <Paper
      elevation={0}
      sx={{
        alignSelf: inbound ? "flex-start" : "flex-end",
        width: "100%",
        maxWidth: { xs: "100%", md: "92%" },
        border: "1px solid",
        borderColor: inbound ? "colors.gray200" : "colors.brand200",
        bgcolor: inbound ? "background.paper" : "colors.brand50",
        borderRadius: 2,
        p: { xs: 1.5, md: 1.75 },
      }}
    >
      <Row sx={{ alignItems: "center", gap: 1.25, mb: 1 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: inbound ? "colors.gray200" : "primary.main",
            color: inbound ? "colors.gray700" : "common.white",
          }}
        >
          {initials(name, phone)}
        </Avatar>
        <Column sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            noWrap
          >
            {name}
          </Typography>
          <Typography variant="caption" sx={{ color: "colors.gray500" }} noWrap>
            {details}
          </Typography>
        </Column>
        <Chip
          size="small"
          label={inbound ? "Inbound" : "Sent"}
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: inbound ? "colors.gray100" : "colors.brand100",
            color: inbound ? "colors.gray700" : "primary.dark",
          }}
        />
      </Row>
      <Divider
        sx={{
          mb: 1.25,
          borderColor: inbound ? "colors.gray200" : "colors.brand200",
        }}
      />
      <Typography
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {message.text || message.snippet}
      </Typography>
    </Paper>
  )
}
